import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  HiOutlineCheckCircle,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineArrowRight,
  HiOutlineSparkles,
  HiOutlineChevronDown,
  HiOutlineBookOpen,
} from "react-icons/hi2";
import { FormButton } from "../../ui";
import { useUser } from "../../hooks";
import { usersApi, settingsApi } from "../../api";
import { canUseAutoBid } from "../../util/plan-gates";
import { APP_ROUTES } from "../../data-access";
import { RootState } from "../../store";
import { appConfig } from "../../app-config";

const UPWORK_PROFILE_URL = "https://www.upwork.com/freelancers/";
const UPWORK_JOBS_URL =
  "https://www.upwork.com/nx/search/jobs/?from_recent_search=true";

const VIDEO_ID = "M2RVdj6frv4";
const VIDEO_TITLE =
  "How to Set Up Auto-Bidding on Upwork with AI (5 Steps) | Upwex Auto-Bid Tutorial";

const DOCS = {
  extension: "https://help.upwex.io/en/articles/install-chrome-extension/",
  sync: "https://help.upwex.io/en/articles/sync-your-upwork-profile/",
  cover: "https://help.upwex.io/en/articles/create-your-first-template/",
  configure: "https://help.upwex.io/en/articles/set-up-auto-bid-rules/",
  launch: "https://help.upwex.io/en/articles/auto-bid-widget/",
};

interface IGuideStep {
  title: string;
  details: string;
  docUrl: string;
  done?: boolean;
  cta?: string;
  external?: boolean;
  action?: () => void;
}

const openExternal = (url: string) =>
  window.open(url, "_blank", "noreferrer");

export const AutoBiddingGetStarted = (): JSX.Element | null => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const canBid = canUseAutoBid(currentUser?.plan);

  const extensionInstalled = useSelector(
    (s: RootState) => s.generic.extensionInstalled
  ) as boolean | undefined;
  const [profileSynced, setProfileSynced] = useState<boolean>(false);
  const [coverReady, setCoverReady] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);

  // Steps 3 & 4 have no reliable backend signal - mark them done once the user
  // has opened the relevant page (persisted per user in localStorage).
  const userId = currentUser?.id;
  const visitKey = (step: number) => `upwex_abguide_step${step}_${userId ?? "anon"}`;
  const readVisit = (step: number): boolean => {
    try {
      return localStorage.getItem(visitKey(step)) === "1";
    } catch {
      return false;
    }
  };
  const [visited3, setVisited3] = useState<boolean>(false);
  const [visited4, setVisited4] = useState<boolean>(false);
  // Which to-do step is expanded (undefined = default to the first incomplete one)
  const [openStep, setOpenStep] = useState<number | null | undefined>(undefined);
  const [videoPlaying, setVideoPlaying] = useState<boolean>(false);

  const markVisited = (step: number, setter: (v: boolean) => void) => {
    try {
      localStorage.setItem(visitKey(step), "1");
    } catch {
      /* ignore */
    }
    setter(true);
  };

  useEffect(() => {
    if (userId) {
      setVisited3(readVisit(3));
      setVisited4(readVisit(4));
    }
    if (!canBid) return;
    let active = true;
    Promise.allSettled([usersApi.getMe(), settingsApi.getSettings()]).then(
      ([me, settings]) => {
        if (!active) return;
        if (me.status === "fulfilled") {
          setProfileSynced(!!me.value.data?.upwork_profile?.userId);
        }
        if (settings.status === "fulfilled") {
          setCoverReady(
            (settings.value as any)?.data?.autobid
              ?.autobid_cover_letter_template_id != null
          );
        }
        setDataLoaded(true);
      }
    );
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canBid, userId]);

  if (!canBid) {
    return (
      <div className="ab-guide ab-guide--locked">
        <span className="ab-guide__lock-icon">
          <HiOutlineSparkles size={26} />
        </span>
        <h2 className="ab-guide__title">Auto-Bidding is part of Flex</h2>
        <p className="ab-guide__sub">
          Let the AI scan Upwork and submit tailored proposals for you, around
          the clock. Upgrade to Flex to turn it on.
        </p>
        <FormButton theme="green-light" onClick={() => navigate(APP_ROUTES.plans)}>
          Upgrade to Flex
        </FormButton>
      </div>
    );
  }

  // Don't render until profile-sync + settings data has loaded, so steps never
  // flash the wrong done/undone state.
  if (!dataLoaded) return null;

  const steps: IGuideStep[] = [
    {
      title: "Install the Upwex extension",
      details:
        "Open the Chrome Web Store and click Add to Chrome, then pin Upwex. The extension is what powers every AI feature directly inside Upwork - without it, Auto-Bidding can't run.",
      docUrl: DOCS.extension,
      done: extensionInstalled === true,
      cta: "Install",
      external: true,
      action: () => openExternal(appConfig.extensionDownloadLink),
    },
    {
      title: "Sync your Upwork profile",
      details:
        "Open your Upwork freelancer profile with the Upwex extension active and click Sync Profile. This lets the AI tailor proposals to your real skills, experience and rate.",
      docUrl: DOCS.sync,
      done: profileSynced,
      cta: "Open Upwork",
      external: true,
      action: () => openExternal(UPWORK_PROFILE_URL),
    },
    {
      title: "Prepare your cover letter",
      details:
        "Go to Cover Letters, pick or create a template, and fill in your Template Data - portfolio links, signature and rates. The AI uses this content when writing each proposal.",
      docUrl: DOCS.cover,
      done: coverReady || visited3,
      cta: "Open Cover Letters",
      action: () => {
        markVisited(3, setVisited3);
        navigate(APP_ROUTES.aiTemplates);
      },
    },
    {
      title: "Configure Auto-Bidding",
      details:
        "In Settings, set your Auto-Bidding templates, Profile Highlights, and Auto-Bidding & Quick Apply preferences - rate, match rules, red flags and daily limits - so the bot bids exactly the way you want.",
      docUrl: DOCS.configure,
      done: visited4,
      cta: "Open Settings",
      action: () => {
        markVisited(4, setVisited4);
        navigate(APP_ROUTES.aiSettings);
      },
    },
    {
      title: "Launch Auto-Bidding on Upwork",
      details:
        "Open Upwork's job feed, open the Upwex panel and switch Auto-Bidding ON. It scans new jobs, scores them against your rules, and submits proposals automatically.",
      docUrl: DOCS.launch,
      cta: "Open Upwork",
      external: true,
      action: () => openExternal(UPWORK_JOBS_URL),
    },
  ];

  const firstIncomplete = steps.findIndex((s) => !s.done);
  const effectiveOpen = openStep === undefined ? firstIncomplete : openStep;

  return (
    <div className="ab-guide">
      <div className="ab-guide__head">
        <h2 className="ab-guide__title">Set up Auto-Bidding</h2>
        <p className="ab-guide__sub">
          Auto-Bidding runs in the Upwex extension on Upwork. Finish these steps
          once and it bids for you automatically.
        </p>
      </div>

      <div className="ab-guide__steps">
        {steps.map((step, i) => {
          // Completed steps stay collapsed - just the check + struck-through title
          if (step.done) {
            return (
              <div key={i} className="ab-guide__step ab-guide__step--done">
                <span className="ab-guide__step-marker">
                  <HiOutlineCheckCircle size={22} />
                </span>
                <span className="ab-guide__step-title">{step.title}</span>
              </div>
            );
          }

          // Incomplete steps are accordions with detailed how-to + docs link
          const open = effectiveOpen === i;
          return (
            <div
              key={i}
              className={`ab-guide__step ab-guide__step--todo ${open ? "ab-guide__step--open" : ""}`}
            >
              <button
                className="ab-guide__step-header"
                onClick={() => setOpenStep(open ? null : i)}
              >
                <span className="ab-guide__step-marker">
                  <span className="ab-guide__step-num">{i + 1}</span>
                </span>
                <span className="ab-guide__step-title">{step.title}</span>
                <HiOutlineChevronDown
                  size={18}
                  className="ab-guide__step-chevron"
                />
              </button>

              {open && (
                <div className="ab-guide__step-body">
                  <p className="ab-guide__step-details">{step.details}</p>
                  <div className="ab-guide__step-actions">
                    <a
                      className="ab-guide__step-doc"
                      href={step.docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <HiOutlineBookOpen size={15} />
                      Read the docs
                    </a>
                    {step.cta && (
                      <button
                        className="ab-guide__step-cta"
                        onClick={step.action}
                      >
                        {step.cta}
                        {step.external ? (
                          <HiOutlineArrowTopRightOnSquare size={14} />
                        ) : (
                          <HiOutlineArrowRight size={14} />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="ab-guide__video">
        <h3 className="ab-guide__video-title">
          Prefer a video? Watch the full walkthrough
        </h3>
        <p className="ab-guide__video-sub">
          See the entire 5-step setup in under a minute.
        </p>
        <div className="ab-guide__video-card">
          {videoPlaying ? (
            <iframe
              src={`https://www.youtube.com/embed/${VIDEO_ID}?rel=0&autoplay=1`}
              title={VIDEO_TITLE}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <button
              type="button"
              className="ab-guide__video-facade"
              onClick={() => setVideoPlaying(true)}
              aria-label="Play the Auto-Bidding tutorial"
            >
              <img
                className="ab-guide__video-thumb"
                src={`https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg`}
                alt={VIDEO_TITLE}
                loading="lazy"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = `https://i.ytimg.com/vi/${VIDEO_ID}/hqdefault.jpg`;
                }}
              />
              <span className="ab-guide__video-play">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
              <span className="ab-guide__video-badge">5 steps · 0:58</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoBiddingGetStarted;
